import yaml from 'js-yaml';

const YAML_BASE = `${process.env.PUBLIC_URL || ''}/what-vervestacks`.replace(/\/$/, '');

function isHtmlResponse(text, contentType) {
  const trimmed = (text || '').trim();
  if (/text\/html/i.test(contentType || '')) return true;
  return (
    trimmed.startsWith('<!DOCTYPE') ||
    trimmed.startsWith('<!doctype') ||
    trimmed.startsWith('<html')
  );
}

function isWhatVervestacksYamlDocument(doc) {
  if (!doc || typeof doc !== 'object' || Array.isArray(doc)) {
    return false;
  }
  return Boolean(
    doc.iso ||
      doc.sections ||
      doc.motivation ||
      doc.country_name ||
      doc.default_country
  );
}

function parseYaml(text) {
  return yaml.load(text);
}

async function fetchYamlText(url) {
  const response = await fetch(url);
  const text = await response.text();
  if (!response.ok) {
    throw new Error(`Failed to load ${url} (${response.status})`);
  }
  return { text, contentType: response.headers.get('content-type') || '' };
}

export async function loadGlobalWhatVervestacksYaml() {
  const { text, contentType } = await fetchYamlText(`${YAML_BASE}/_global.yaml`);
  if (isHtmlResponse(text, contentType)) {
    throw new Error('Failed to load _global.yaml (invalid response)');
  }
  const doc = parseYaml(text);
  if (!isWhatVervestacksYamlDocument(doc)) {
    throw new Error('Failed to parse _global.yaml');
  }
  return doc;
}

/**
 * Returns parsed ISO yaml or null if file missing / invalid (e.g. CRA index.html).
 */
async function loadIsoWhatVervestacksYaml(isoCode) {
  const normalized = String(isoCode || '').trim().toLowerCase();
  if (!normalized || !/^[a-z]{3}$/i.test(normalized)) {
    return null;
  }

  const url = `${YAML_BASE}/${normalized}.yaml`;

  try {
    const response = await fetch(url);

    // A missing ISO file is expected: fall back to _global.yaml.
    // Some servers (e.g. IIS) redirect 404s to an external error page,
    // so treat any redirect or non-OK status as "missing" rather than fatal.
    if (response.redirected || !response.ok) {
      return null;
    }

    const text = await response.text();
    const contentType = response.headers.get('content-type') || '';

    if (isHtmlResponse(text, contentType)) {
      return null;
    }

    const doc = parseYaml(text);
    if (!isWhatVervestacksYamlDocument(doc)) {
      return null;
    }

    return doc;
  } catch (err) {
    // Network error / blocked cross-origin redirect: ISO file is unavailable.
    return null;
  }
}

/**
 * Either ISO yaml or _global.yaml — never both for rendering.
 */
export async function loadWhatVervestacksYaml(isoCode, dashboardCountryName) {
  const normalized = String(isoCode || '').trim().toLowerCase();
  const isoPanel = await loadIsoWhatVervestacksYaml(isoCode);

  if (isoPanel) {
    return {
      panel: isoPanel,
      source: 'iso',
      yamlFile: `${normalized}.yaml`,
      displayName:
        isoPanel.country_name || dashboardCountryName || 'this country',
    };
  }

  const globalPanel = await loadGlobalWhatVervestacksYaml();
  return {
    panel: globalPanel,
    source: 'global',
    yamlFile: '_global.yaml',
    displayName:
      dashboardCountryName ||
      globalPanel.default_country?.country_name ||
      'this country',
  };
}
